import React, { useState, FormEvent, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { useMutation } from '@apollo/react-hooks';
import analyticsService, { AmplitudeEventData, AnalyticsEvent } from 'services/AnalyticsService';
import { MuiBox, MuiCheckbox, MuiFormControl, MuiFormControlLabel } from 'theme/material-ui';
import { IconSize, FontSize, FontWeight } from 'modules/styles/variables';
import { Icon } from 'components/Icon';
import Typography from 'components/UI/Typography';
import Spacer from 'components/UI/Layout/Spacer';
import StyledButton from 'components/UI/Button/StyledButton';
import YesNoRadioGroup from 'components/PreRegister/YesNoRadioGroup';
import SpinnerModal from 'components/UI/Spinner/SpinnerModal';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { RootState } from 'store/types';
import {
  CheckListItem,
  VisitDetail,
  PatientInfo,
  UpdateSkipPaymentFlagPayload
} from 'store/preRegistration/types';
import {
  currentLocationPathNameSelector,
  previousLocationPathNameSelector
} from 'store/router/selectors';
import {
  PreRegistrationVisitDetailSelector,
  PreRegistrationFetchingSelector,
  PreRegistrationCheckListStepByIdSelector,
  PreRegistrationPatientInfoSelector
} from 'store/preRegistration/selectors';
import {
  UPDATE_PRE_REGISTRATION_CHECKLIST,
  updateSkipPaymentFlag
} from 'store/preRegistration/actions';
import { UPDATE_CHECKLIST_FORM } from 'services/orchestration/mutations';
import { SkipPaymentFlags } from 'lib/constants/preRegister';
import { CheckListItemPageProps } from '../ConsentForm/types';
import { MSPQRadioGroupOptions, errors } from '../constant';
import {
  getMSPQRadioGroups,
  checkFormReady,
  generateQuestionList,
  headerWithJWT,
  handleError
} from '../utils';
import {
  MSPQCheckBoxStateType,
  MSPQCareCheckBoxStateType,
  MSPQRetirementStateType,
  MSPQRadioGroupStateType
} from '../types';
import { PreRegHeader } from '../common';

export const initialRadioGroupState: MSPQRadioGroupStateType = MSPQRadioGroupOptions.reduce(
  (state, option) => {
    return { ...state, [option.key]: null };
  },
  {} as MSPQRadioGroupStateType
);

export const initialCheckBoxState: MSPQCheckBoxStateType = {
  age: false,
  disability: false,
  esrd: false
};

export const initialCareCheckBoxState: MSPQCareCheckBoxStateType = {
  hospice: false,
  home: false,
  nursing: false,
  none: false
};

const initialRetirementState = {
  employed: null,
  spouseEmployed: null
};

interface Props {
  step: CheckListItem;
  isLoading: boolean;
  visitDetail: VisitDetail;
  patientInfo: PatientInfo;
  currentUrl: string;
  referringUrl: string;
  updateFlag: (payload: UpdateSkipPaymentFlagPayload) => void;
}

export const MSPQForm = ({
  step,
  isLoading,
  visitDetail,
  patientInfo,
  updateFlag,
  currentUrl,
  referringUrl
}: Props) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const id = location.state?.id;
    if (!id) {
      history.replace('/pre-reg/checklist');
    }
  }, []);

  const [updateChecklist, { loading: updateLoading }] = useMutation(UPDATE_CHECKLIST_FORM);
  const [checkBoxState, setCheckBoxState] = useState<MSPQCheckBoxStateType>(initialCheckBoxState);
  const [radioGroupState, setRadioGroupState] = useState<MSPQRadioGroupStateType>(
    initialRadioGroupState
  );
  const [careCheckBoxState, setCareCheckBoxState] = useState<MSPQCareCheckBoxStateType>(
    initialCareCheckBoxState
  );

  const [retirementDateState, setRetirementDateState] = useState<MSPQRetirementStateType>(
    initialRetirementState
  );

  const onClose = () => history.push('/pre-reg/checklist');

  const onConfirmButtonPress = async () => {
    try {
      if (step.complete) {
        handleError(errors.Complete, history);
      } else {
        const questionList = generateQuestionList(
          checkBoxState,
          radioGroupState,
          careCheckBoxState,
          retirementDateState
        );

        const fin = visitDetail.finNumber;
        const mrn = patientInfo?.id;

        const mutationPayload = {
          arType: visitDetail.arType,
          visitType: visitDetail.type,
          checkListStepId: step.id,
          facilityCode: visitDetail.facilityCode,
          fin,
          mrn,
          stepData: {
            questionList,
            aliasId: step.docInfo.docId || ''
          }
        };

        const res = await updateChecklist({
          variables: {
            payload: mutationPayload
          },
          context: {
            clientName: 'orchestration',
            headers: headerWithJWT()
          }
        });

        if (res.data.updateChecklistConsentFormStep.success) {
          dispatch({
            type: UPDATE_PRE_REGISTRATION_CHECKLIST,
            payload: res.data.updateChecklistConsentFormStep.data
          });

          const hasException =
            checkBoxState.disability ||
            checkBoxState.esrd ||
            radioGroupState.blackLung === 'yes' ||
            radioGroupState.dva === 'yes' ||
            radioGroupState.employed === 'yes' ||
            radioGroupState.governmentSearch === 'yes' ||
            radioGroupState.groupHealth === 'yes' ||
            radioGroupState.spouseEmployed === 'yes' ||
            radioGroupState.workRelated === 'yes' ||
            careCheckBoxState.home ||
            careCheckBoxState.hospice ||
            careCheckBoxState.nursing;
          updateFlag({ key: SkipPaymentFlags.MSPQ_EXCEPTION, value: hasException });

          const medicareBaseOn = Object.keys(checkBoxState)
            .filter(key => checkBoxState[key as keyof MSPQCheckBoxStateType])
            .join(', ');

          const baseOnAgeException = Object.keys(radioGroupState).some(
            key => radioGroupState[key as keyof MSPQRadioGroupStateType] === 'yes'
          );

          const seeRegistrarText =
            hasException || baseOnAgeException ? 'See registrar' : "Don't see registrar";

          const amplitudeEventData: AmplitudeEventData = {
            currentUrl,
            referringUrl,
            /* eslint-disable-next-line @typescript-eslint/camelcase */
            form_location: visitDetail?.location?.name || '',
            /* eslint-disable-next-line @typescript-eslint/camelcase */
            form_detail_type: `Hospital Registration: ${step.title}`,
            /* eslint-disable-next-line @typescript-eslint/camelcase */
            form_detail_additional_info: `Medicare based on ${medicareBaseOn}. ${seeRegistrarText}`
          };
          analyticsService.logEvent(AnalyticsEvent.HospitalFormDetailComplete, amplitudeEventData);

          history.push('/pre-reg/checklist');
        } else {
          handleError(errors[step.type], history);
        }
      }
    } catch (err) {
      handleError(errors[step.type], history);
    }
  };

  const onRadioButtonChange = (event: FormEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setRadioGroupState({ ...radioGroupState, [name]: value });
  };

  const handleCheckBoxChange = (event: FormEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setCheckBoxState({ ...checkBoxState, [name]: checked });
  };

  const handleCareCheckBoxChange = (event: FormEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    if (checked) {
      if (name === 'none') {
        setCareCheckBoxState({ ...initialCareCheckBoxState, none: true });
      } else {
        setCareCheckBoxState({ ...careCheckBoxState, none: false, [name]: checked });
      }
    } else {
      setCareCheckBoxState({ ...careCheckBoxState, [name]: checked });
    }
  };

  const renderCheckBoxGroup = () => {
    const { age, disability, esrd } = checkBoxState;

    return (
      <MuiBox p={2.5}>
        <MuiFormControl component="fieldset">
          <Typography fontSize={FontSize.base} data-testid="mspq-entitled-label">
            Are you entitled to Medicare based on:
          </Typography>
          <Spacer spacing="medium" />
          <MuiFormControlLabel
            checked={age}
            control={
              <MuiCheckbox
                color="primary"
                data-testid="mspq-age-checkbox"
                onChange={handleCheckBoxChange}
                name="age"
              />
            }
            label="Age?"
            data-testid="mspq-age-label"
          />
          <MuiFormControlLabel
            checked={disability}
            control={
              <MuiCheckbox
                color="primary"
                data-testid="mspq-disability-checkbox"
                onChange={handleCheckBoxChange}
                name="disability"
              />
            }
            label="Disability?"
            data-testid="mspq-disability-label"
          />
          <MuiFormControlLabel
            checked={esrd}
            control={
              <MuiCheckbox
                color="primary"
                data-testid="mspq-esrd-checkbox"
                onChange={handleCheckBoxChange}
                name="esrd"
              />
            }
            label="ESRD (End Stage Renal Disease/kidney failure)?"
            data-testid="mspq-esrd-label"
          />
        </MuiFormControl>
      </MuiBox>
    );
  };

  const renderRadioGroups = () => {
    const radioGroups = getMSPQRadioGroups(MSPQRadioGroupOptions, radioGroupState, checkBoxState);
    return (
      <MuiBox px={2.5}>
        {radioGroups.map(group => {
          const { title, key, showDate } = group;
          return (
            <MuiBox key={key}>
              <YesNoRadioGroup
                name={key}
                value={radioGroupState[key]}
                onChange={onRadioButtonChange}
                formLabel={title}
              />
              {showDate ? (
                <MuiBox px={3.5}>
                  <Typography fontSize={FontSize.base} data-testid="mspq-date-of-retirement-label">
                    If applicable, date of retirement?
                  </Typography>
                  <KeyboardDatePicker
                    name={key}
                    disableToolbar
                    fullWidth
                    variant="dialog"
                    inputVariant="standard"
                    placeholder="mm/dd/yyyy"
                    format="MM/DD/YYYY"
                    value={retirementDateState[key]}
                    maxDate={Date.now()}
                    onChange={value =>
                      setRetirementDateState({ ...retirementDateState, [key]: value })
                    }
                    data-testid={`${key}--date-picker-input`}
                  />
                </MuiBox>
              ) : null}
              <Spacer spacing="small" />
              <Spacer spacing="large" />
            </MuiBox>
          );
        })}
      </MuiBox>
    );
  };

  const renderCareCheckBoxGroup = () => {
    const { age, disability, esrd } = checkBoxState;
    const { hospice, home, nursing, none } = careCheckBoxState;

    const lastOptionKey = MSPQRadioGroupOptions[MSPQRadioGroupOptions.length - 1].key;
    const lastOptionValue = radioGroupState[lastOptionKey];

    if (!lastOptionValue || disability || esrd || !age) {
      return null;
    }

    return (
      <MuiBox px={2.5} pb={2.5}>
        <MuiFormControl component="fieldset">
          <Typography fontSize={FontSize.base} data-testid="mspq-are-you-receiving-label">
            Are you receiving:
          </Typography>
          <Spacer spacing="medium" />
          <MuiFormControlLabel
            checked={hospice}
            control={
              <MuiCheckbox
                color="primary"
                onChange={handleCareCheckBoxChange}
                name="hospice"
                data-testid="mspq-hospice-checkbox"
              />
            }
            label="Hospice Care?"
            data-testid="mspq-hospice-label"
          />
          <MuiFormControlLabel
            checked={home}
            control={
              <MuiCheckbox
                color="primary"
                onChange={handleCareCheckBoxChange}
                name="home"
                data-testid="mspq-home-checkbox"
              />
            }
            label="Home Health Care?"
            data-testid="mspq-home-label"
          />
          <MuiFormControlLabel
            checked={nursing}
            control={
              <MuiCheckbox
                color="primary"
                onChange={handleCareCheckBoxChange}
                name="nursing"
                data-testid="mspq-nursing-checkbox"
              />
            }
            label="Skilled Nursing Facility?"
            data-testid="mspq-nursing-label"
          />
          <MuiFormControlLabel
            checked={none}
            control={
              <MuiCheckbox
                color="primary"
                onChange={handleCareCheckBoxChange}
                name="none"
                data-testid="mspq-none-checkbox"
              />
            }
            label="None"
            data-testid="mspq-none-label"
          />
        </MuiFormControl>
      </MuiBox>
    );
  };

  const renderMessage = () => {
    const { disability, esrd } = checkBoxState;
    if (!disability && !esrd) {
      return null;
    }

    return (
      <MuiBox px={2.5} pb={2.5}>
        <Typography
          fontSize={FontSize.medium}
          fontWeight={FontWeight.bold}
          data-testid="mspq-thankyou-label"
        >
          Thank You!
        </Typography>
        <Spacer spacing="medium" />
        <Typography fontSize={FontSize.medium} data-testid="mspq-followup-label">
          An intermountain employee will need to follow up with you about your Medicare coverage, by
          telephone or in-person at your appointment.
        </Typography>
      </MuiBox>
    );
  };

  const isFormReady = checkFormReady(checkBoxState, careCheckBoxState);

  if (isLoading || updateLoading) {
    return <SpinnerModal isLoading={isLoading || updateLoading} />;
  }

  return (
    <MuiBox minHeight="100%" display="flex" flexDirection="column">
      <PreRegHeader title="Medicare Questions" />

      {renderCheckBoxGroup()}
      {renderRadioGroups()}
      {renderCareCheckBoxGroup()}
      {renderMessage()}
      <MuiBox
        px={3}
        py={6}
        display="flex"
        flexGrow={1}
        flexDirection="column"
        justifyContent="flex-end"
      >
        <StyledButton
          data-testid="submit-button"
          onPress={onConfirmButtonPress}
          disabled={!isFormReady}
          title="confirm"
        >
          Confirm
        </StyledButton>
      </MuiBox>
    </MuiBox>
  );
};

const mapStateToProps = (state: RootState, props: CheckListItemPageProps) => {
  const id = props.location.state?.id || '';
  return {
    step: PreRegistrationCheckListStepByIdSelector(id)(state),
    visitDetail: PreRegistrationVisitDetailSelector(state),
    patientInfo: PreRegistrationPatientInfoSelector(state),
    isLoading: PreRegistrationFetchingSelector(state),
    currentUrl: currentLocationPathNameSelector(state),
    referringUrl: previousLocationPathNameSelector(state)
  };
};

const mapDispatchToProps = {
  updateFlag: updateSkipPaymentFlag
};

export default connect(mapStateToProps, mapDispatchToProps)(MSPQForm);
